import api from './api';

export type TipoEnvioProgramado =
  | 'prueba'
  | 'informe_personalizado'
  | 'programacion_produccion'
  | 'ordenes_pago_dia';

export type FrecuenciaEnvio = 'diaria' | 'semanal' | 'mensual';

export type PeriodoEnvio =
  | 'hoy'
  | 'ayer'
  | 'semana_actual'
  | 'semana_anterior'
  | 'mes_actual'
  | 'mes_anterior'
  | 'ultimos_n_dias';

export interface ProgramadorEnvioConfig {
  destinatarios: string[];
  cc: string[];
  asunto: string;
  cuerpo: string;
  siVacio: 'omitir' | 'enviar';
  informeId: number | null;
  informeModulo: string;
  recalcularAntes: boolean;
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  nroUbicacion: number | null;
  ventanaDias: number;
  horizonteDias: number;
  ajusteDemandaPct: number;
  incluirYaEnviadas: boolean;
}

export interface ProgramadorEnvio {
  id: number;
  nombre: string;
  activa: boolean;
  tipo: TipoEnvioProgramado;
  frecuencia: FrecuenciaEnvio;
  diasSemana: number[];
  diaMes: number | null;
  hora: number;
  minuto: number;
  periodo: PeriodoEnvio;
  ultimosNDias: number;
  proximaEjecucion: string;
  ultimaEjecucion: string | null;
  ultimoEstado: string | null;
  usuario: string;
  configuracion: ProgramadorEnvioConfig;
}

export interface ProgramadorEnvioEjecucion {
  id: number;
  ocurrenciaLocal: string;
  estado: string;
  inicio: string;
  fin: string | null;
  mensajesEnviados: number;
  omitidos: number;
  destinatarios: string | null;
  error: string | null;
  manual: boolean;
}

const BASE = '/api/programador-envios';

const lista = <T,>(data: unknown): T[] => (Array.isArray(data) ? data as T[] : []);

const programadorEnviosService = {
  list: async (): Promise<ProgramadorEnvio[]> => {
    const response = await api.get(BASE);
    return lista<ProgramadorEnvio>(response.data);
  },
  crear: async (payload: Omit<ProgramadorEnvio, 'id' | 'proximaEjecucion' | 'ultimaEjecucion' | 'ultimoEstado' | 'usuario'>): Promise<ProgramadorEnvio> => {
    const response = await api.post(BASE, aApi(payload));
    return response.data as ProgramadorEnvio;
  },
  actualizar: async (id: number, payload: Omit<ProgramadorEnvio, 'id' | 'proximaEjecucion' | 'ultimaEjecucion' | 'ultimoEstado' | 'usuario'>): Promise<ProgramadorEnvio> => {
    const response = await api.put(`${BASE}/${id}`, aApi(payload));
    return response.data as ProgramadorEnvio;
  },
  cambiarActiva: async (id: number, activa: boolean): Promise<void> => {
    await api.put(`${BASE}/${id}/activa`, { activa });
  },
  eliminar: async (id: number): Promise<void> => {
    await api.delete(`${BASE}/${id}`);
  },
  ejecutar: async (id: number): Promise<ProgramadorEnvioEjecucion> => {
    const response = await api.post(`${BASE}/${id}/ejecutar`);
    return response.data as ProgramadorEnvioEjecucion;
  },
  historial: async (id: number): Promise<ProgramadorEnvioEjecucion[]> => {
    const response = await api.get(`${BASE}/${id}/ejecuciones`);
    return lista<ProgramadorEnvioEjecucion>(response.data);
  },
};

function aApi(payload: Omit<ProgramadorEnvio, 'id' | 'proximaEjecucion' | 'ultimaEjecucion' | 'ultimoEstado' | 'usuario'>) {
  return {
    nombre: payload.nombre,
    activa: payload.activa,
    tipo: payload.tipo,
    frecuencia: payload.frecuencia,
    diasSemana: payload.diasSemana,
    diaMes: payload.diaMes,
    hora: payload.hora,
    minuto: payload.minuto,
    periodo: payload.periodo,
    ultimosNDias: payload.ultimosNDias,
    configuracion: {
      destinatarios: payload.configuracion.destinatarios,
      cc: payload.configuracion.cc,
      asunto: payload.configuracion.asunto,
      cuerpo: payload.configuracion.cuerpo,
      siVacio: payload.configuracion.siVacio,
      informeId: payload.configuracion.informeId,
      informeModulo: payload.configuracion.informeModulo || null,
      recalcularAntes: payload.configuracion.recalcularAntes,
      codigoRamo: payload.configuracion.codigoRamo || null,
      codigoLinea: payload.configuracion.codigoLinea || null,
      codigoClase: payload.configuracion.codigoClase || null,
      nroUbicacion: payload.configuracion.nroUbicacion,
      ventanaDias: payload.configuracion.ventanaDias,
      horizonteDias: payload.configuracion.horizonteDias,
      ajusteDemandaPct: payload.configuracion.ajusteDemandaPct,
      incluirYaEnviadas: payload.configuracion.incluirYaEnviadas,
    },
  };
}

export default programadorEnviosService;
